// Pre-game web UI (outside Phaser): Landing / Register / Login. A full-screen DOM overlay (the
// same vanilla-DOM-overlay pattern as the rotate notice) shown before the game when not signed
// in; on entry it removes itself and the Phaser game (already mounted behind) is revealed.
// Register/login authenticate against the backend (handle + password → session token), so a
// registered account's wallet is token-protected. Flag-agnostic; never touches the sim/match.
// i18n-reactive (re-renders on locale change). A guest option enters without an account.

import { getLocale, LOCALES, type Locale, onLocaleChange, setLocale, t } from '../i18n'
import { audio } from '../game/JKGame_audio'
import { continueAsGuest, isSignedIn, signIn, validateHandle } from '../game/JKGame_account'
import { apiLogin, apiRegister } from '../services/JKGame_economy'

type Page = 'landing' | 'register' | 'login'

/** Mount the landing/auth overlay if the player is not signed in. No-op when already signed in
 *  (straight to the game) or off-DOM. */
export function mountAuthOverlay(): void {
  if (typeof document === 'undefined' || isSignedIn()) return

  const root = document.createElement('div')
  root.id = 'JKGame_auth'
  document.body.appendChild(root)
  let page: Page = 'landing'

  const unsub = onLocaleChange(() => render())
  const enter = (): void => {
    audio.play('success')
    unsub()
    root.remove()
  }

  const localeRow = (): string => {
    const cur = getLocale()
    const btns = LOCALES.map(
      (l) => `<button class="auth-loc${l.id === cur ? ' is-active' : ''}" data-loc="${l.id}">${l.label}</button>`,
    ).join('')
    return `<div class="auth-locale">${btns}</div>`
  }

  const template = (): string => {
    if (page === 'register') {
      return `<div class="auth-card">
        <h2>${t('auth.register.title')}</h2>
        <label class="auth-label">${t('auth.handle.label')}</label>
        <input id="auth-handle" class="auth-input" autocomplete="off" autocapitalize="off" spellcheck="false" placeholder="${t('auth.handle.ph')}" />
        <label class="auth-label">${t('auth.password.label')}</label>
        <input id="auth-pass" class="auth-input" type="password" autocomplete="new-password" placeholder="${t('auth.password.ph')}" />
        <label class="auth-label">${t('auth.name.label')}</label>
        <input id="auth-name" class="auth-input" autocomplete="off" placeholder="${t('auth.name.ph')}" />
        <p class="auth-err" hidden></p>
        <button class="auth-btn auth-primary" data-act="do-register">${t('auth.register.submit')}</button>
        <button class="auth-btn" data-act="back">${t('auth.back')}</button>
      </div>`
    }
    if (page === 'login') {
      return `<div class="auth-card">
        <h2>${t('auth.login.title')}</h2>
        <label class="auth-label">${t('auth.handle.label')}</label>
        <input id="auth-handle" class="auth-input" autocomplete="off" autocapitalize="off" spellcheck="false" placeholder="${t('auth.handle.ph')}" />
        <label class="auth-label">${t('auth.password.label')}</label>
        <input id="auth-pass" class="auth-input" type="password" autocomplete="current-password" placeholder="${t('auth.password.ph')}" />
        <p class="auth-err" hidden></p>
        <button class="auth-btn auth-primary" data-act="do-login">${t('auth.login.submit')}</button>
        <button class="auth-btn" data-act="back">${t('auth.back')}</button>
      </div>`
    }
    return `<div class="auth-card">
      <h1 class="auth-title">${t('app.title')}</h1>
      <p class="auth-sub">${t('app.subtitle')}</p>
      <button class="auth-btn auth-primary" data-act="guest">${t('auth.guest')}</button>
      <button class="auth-btn" data-act="login">${t('auth.login')}</button>
      <button class="auth-btn" data-act="register">${t('auth.register')}</button>
      ${localeRow()}
    </div>`
  }

  const showErr = (msg: string): void => {
    const el = root.querySelector<HTMLParagraphElement>('.auth-err')
    if (el) {
      el.textContent = msg
      el.hidden = false
    }
  }

  let busy = false

  // Map a backend reason → localized message. Reasons: bad_handle, bad_password:<why>,
  // handle_taken, bad_credentials; anything else (incl. network) → a generic offline notice.
  const reasonMsg = (reason: string | undefined): string => {
    if (reason === 'bad_handle') return t('auth.invalidHandle')
    if (reason?.startsWith('bad_password')) return t('auth.err.bad_password')
    if (reason === 'handle_taken') return t('auth.err.handle_taken')
    if (reason === 'bad_credentials') return t('auth.err.bad_credentials')
    return t('auth.err.offline')
  }

  const submit = async (act: 'do-register' | 'do-login'): Promise<void> => {
    if (busy) return
    const handle = (root.querySelector<HTMLInputElement>('#auth-handle')?.value ?? '').trim()
    const password = root.querySelector<HTMLInputElement>('#auth-pass')?.value ?? ''
    if (!validateHandle(handle)) {
      showErr(t('auth.invalidHandle'))
      return
    }
    busy = true
    showErr(t('auth.pending'))
    try {
      const name = act === 'do-register' ? (root.querySelector<HTMLInputElement>('#auth-name')?.value ?? '').trim() : ''
      const res = act === 'do-register' ? await apiRegister(handle, password, name) : await apiLogin(handle, password)
      if (res.ok && res.token && res.handle) {
        signIn(res.handle, res.token, name) // store the normalized handle + session token
        enter()
        return
      }
      audio.play('error')
      showErr(reasonMsg(res.reason))
    } catch {
      audio.play('error')
      showErr(t('auth.err.offline')) // backend unreachable — never a silent failure
    } finally {
      busy = false
    }
  }

  const render = (): void => {
    root.innerHTML = template()
    root.querySelectorAll<HTMLElement>('[data-act],[data-loc]').forEach((el) => {
      el.addEventListener('click', () => {
        audio.play('uiTap')
        const loc = el.dataset.loc as Locale | undefined
        if (loc) {
          setLocale(loc) // onLocaleChange → re-render
          return
        }
        switch (el.dataset.act) {
          case 'guest':
            continueAsGuest()
            enter()
            break
          case 'login':
            page = 'login'
            render()
            break
          case 'register':
            page = 'register'
            render()
            break
          case 'back':
            page = 'landing'
            render()
            break
          case 'do-register':
            void submit('do-register')
            break
          case 'do-login':
            void submit('do-login')
            break
        }
      })
    })
  }

  render()
}
